/**
 * „So rechnet …“ zum gewählten Profil (Board „Preisautomatik · Einstellungen“), aufgeklappt unter den
 * Karten statt als Popover: vier Grenzen und die Last-Minute-Stufen aus `lastMinuteSteps`.
 */
import { X } from 'lucide-react';
import type { PricingStrategyProfileResponse } from '@/shared/api';
import { STRATEGY_LABEL } from './ProfileChoiceCards';
import { formatProfilePercent } from './profileValueFormat';

const WINDOW: Record<PricingStrategyProfileResponse['lastMinuteSteps'][number]['window'], string> =
  {
    MoreThanFourDays: 'mehr als 4 Tage',
    Day4: 'Tag 4',
    Day3: 'Tag 3',
    Day2: 'Tag 2',
    Day1: 'Tag 1',
    Day0BeforeNoon: 'Tag 0 bis 11:59',
    Day0Afternoon: 'Tag 0 12:00–17:59',
    Day0Evening: 'Tag 0 ab 18:00',
  };

export function ProfileExplanation({
  id,
  profile,
  onClose,
}: {
  id: string;
  profile: PricingStrategyProfileResponse;
  onClose: () => void;
}) {
  const label = STRATEGY_LABEL[profile.strategy];
  const limits = [
    ['Belegung senkt bis', profile.maximumNegativeOccupancyAdjustmentPercent],
    ['Belegung hebt bis', profile.maximumPositiveOccupancyAdjustmentPercent],
    ['Untere Grenze', profile.softCorridorMinimumPercent],
    ['Obere Grenze', profile.softCorridorMaximumPercent],
  ] as const;
  const steps = [...profile.lastMinuteSteps]
    .sort((a, b) => a.position - b.position)
    .map(
      (step) =>
        `${WINDOW[step.window]} ${formatProfilePercent(step.directAdjustmentPercent)} · Freigabe ${formatProfilePercent(step.negativeOccupancyReleasePercent)}`,
    );
  return (
    <section
      id={id}
      aria-label={`So rechnet „${label}“`}
      className="bg-paper flex flex-col gap-2 rounded-lg p-4"
    >
      <span className="flex items-center gap-2">
        <span className="grow text-[15px] font-bold">So rechnet „{label}“</span>
        <button
          type="button"
          aria-label="Erklärung schließen"
          onClick={onClose}
          className="border-line bg-card grid size-11 place-items-center rounded-full border"
        >
          <X size={16} aria-hidden />
        </button>
      </span>
      <span className="text-ink text-[13px] leading-normal">
        Ausgangspunkt ist der Referenzpreis. Belegung, Vorlauf, Wochentag und Lücken verschieben ihn
        innerhalb fester Grenzen; Mindest- und Maximalpreis gelten immer.
      </span>
      <dl className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
        {limits.map(([name, value]) => (
          <div key={name} className="bg-card rounded-control flex flex-col px-2.5 py-2">
            <dt className="text-ink-soft text-[11px]">{name}</dt>
            <dd className="font-bold">{formatProfilePercent(value)}</dd>
          </div>
        ))}
      </dl>
      <span className="text-ink-soft text-xs">Last Minute: {steps.join('; ')}</span>
    </section>
  );
}
